// 카피바라 3D 모델 (머리 아이템 제외). 프로토타입 927–998.
import * as THREE from 'three';
import { plush } from '../engine/materials';
import { S, T, lineGeo, mergeGeo, part } from '../engine/geometry';

export const FUR = {
  classic: { label: 'Classic', fur: '#A6693A', rim: '#E8B27E', dark: '#5E321D' },
  honey: { label: 'Honey', fur: '#BF884E', rim: '#F2CC98', dark: '#71412A' },
  cocoa: { label: 'Cocoa', fur: '#7E4E2E', rim: '#C9946A', dark: '#3F2214' },
} as const;
export type FurTone = keyof typeof FUR;

export interface CapyMats { fur: THREE.MeshStandardMaterial; dark: THREE.MeshStandardMaterial }

/** 눈·입 선 (소품 눈에도 씀). */
export const inkMat = new THREE.MeshStandardMaterial({ color: '#2A170D', roughness: 0.9 });
const toneMats: Partial<Record<FurTone, CapyMats>> = {};

/** 털 색 머티리얼. own이면 공유하지 않고 새로 만든다 (내 카피바라 털 바꾸기용). */
export function matsFor(tone: FurTone, own = false): CapyMats {
  const cached = toneMats[tone];
  if (!own && cached) return cached;
  const f = FUR[tone];
  const m = { fur: plush(f.fur, f.rim, 0.2), dark: plush(f.dark, '#A0623F', 0.12) };
  if (!own) toneMats[tone] = m;
  return m;
}

const bodyGeo = (() => {
  const pr = [[0, 0], [0.5, 0.03], [0.82, 0.16], [0.97, 0.42], [0.99, 0.72], [0.92, 1.02], [0.8, 1.28], [0.68, 1.5], [0.5, 1.72], [0, 1.8]]
    .map(([x, y]) => new THREE.Vector2(x, y));
  const g = new THREE.LatheGeometry(pr, 36);
  g.computeVertexNormals();
  return g;
})();
const headGeo = (() => {
  const g = new THREE.SphereGeometry(0.82, 32, 22), hp = g.attributes.position!;
  for (let i = 0; i < hp.count; i++) {
    let x = hp.getX(i), y = hp.getY(i), z = hp.getZ(i);
    const k = y / 0.82;
    x *= 1 - 0.16 * k;
    z *= 1 - 0.05 * k;
    if (y > 0.56) y = 0.56 + (y - 0.56) * 0.55;
    hp.setXYZ(i, x, y, z);
  }
  g.computeVertexNormals();
  return g;
})();
const muzzleGeo = (() => {
  const g = new THREE.SphereGeometry(0.5, 24, 16), mp = g.attributes.position!;
  for (let i = 0; i < mp.count; i++) mp.setX(i, mp.getX(i) * (1 - 0.14 * mp.getY(i) / 0.5));
  g.computeVertexNormals();
  return g;
})();
const eyeGeo = new THREE.CylinderGeometry(0.016, 0.016, 0.15, 8);

// 미리 합쳐 둔 공유 부품: 몸+허벅지, 주둥이+귀, 얼굴 선 전부
const bodyMergedGeo = mergeGeo([
  T(bodyGeo, (m) => m.scale.set(1, 1, 0.9)),
  ...[1, -1].map((s) => T(S(0.38, 20, 14), (m) => m.position.set(s * 0.48, 0.36, 0.38))),
]);
const headDarkGeo = mergeGeo([
  T(muzzleGeo, (m) => { m.scale.set(0.98, 0.84, 0.72); m.position.set(0, -0.14, 0.74); }),
  ...[1, -1].map((s) => T(S(0.12, 20, 14), (m) => { m.scale.set(1, 1, 0.6); m.position.set(s * 0.5, 0.5, -0.32); m.rotation.z = s * 0.6; })),
]);
const inkGeo = (() => {
  const tmpRoot = new THREE.Group(), list: THREE.Mesh[] = [];
  const add = (m: THREE.Mesh) => { tmpRoot.add(m); list.push(m); return m; };
  [1, -1].forEach((s) => { add(T(S(0.028, 12, 8), (m) => { m.scale.set(0.75, 2.1, 0.5); m.position.set(s * 0.12, 0.1, 1.012); m.rotation.set(-0.35, 0, -s * 0.3); })); });
  add(T(lineGeo(0.2), (m) => { m.position.set(0, -0.07, 1.088); m.rotation.x = -0.15; }));
  [1, -1].forEach((s) => { add(T(lineGeo(0.11), (m) => { m.position.set(s * 0.04, -0.205, 1.097); m.rotation.z = s * 0.85; })); });
  [1, -1].forEach((s) => {
    const eye = new THREE.Group();
    eye.position.set(s * 0.51, 0.29, 0.6);
    eye.rotation.y = s * Math.atan2(0.53, 0.62);
    eye.rotation.x = -0.35;
    tmpRoot.add(eye);
    const lid = T(eyeGeo, (m) => { m.rotation.z = Math.PI / 2 + s * 0.55; });
    eye.add(lid);
    list.push(lid);
  });
  tmpRoot.updateMatrixWorld(true);
  return mergeGeo(list);
})();

export interface CapyModel {
  root: THREE.Group;
  bodyG: THREE.Group;
  body: THREE.Mesh;
  feet: THREE.Mesh[];
  arms: THREE.Group[];
  paws: THREE.Mesh[];
  headPivot: THREE.Group;
  headG: THREE.Group;
  mouth: THREE.Mesh;
  itemAnchor: THREE.Group;
}

export function makeCapy(mats: CapyMats): CapyModel {
  const root = new THREE.Group();
  const bodyG = new THREE.Group(); root.add(bodyG);
  const body = part(bodyMergedGeo, mats.fur, true); bodyG.add(body);
  const feet: THREE.Mesh[] = [];
  [1, -1].forEach((s) => {
    const foot = part(S(0.22, 16, 10), mats.dark);
    foot.scale.set(0.95, 0.55, 1.3);
    foot.position.set(s * 0.45, 0.1, 0.78);
    bodyG.add(foot);
    feet.push(foot);
  });
  const arms: THREE.Group[] = [], paws: THREE.Mesh[] = [];
  [1, -1].forEach((s) => {
    const pivot = new THREE.Group(); pivot.position.set(s * 0.55, 1.38, 0.45); bodyG.add(pivot);
    const limb = new THREE.Group();
    limb.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(s * 0.3, -0.55, 0.78).normalize());
    pivot.add(limb);
    const arm = part(S(0.16, 14, 10), mats.fur); arm.scale.set(0.9, 1.6, 0.9); arm.position.y = 0.2; limb.add(arm);
    const paw = part(S(0.125, 12, 8), mats.dark); paw.scale.set(1, 0.8, 1); paw.position.y = 0.42; limb.add(paw); paws.push(paw);
    arms.push(pivot);
  });
  const headPivot = new THREE.Group(); headPivot.position.set(0, 1.72, 0); bodyG.add(headPivot);
  const headG = new THREE.Group(); headG.position.set(0, 0.25, 0.15); headPivot.add(headG);
  const head = part(headGeo, mats.fur, true); head.scale.set(0.98, 0.9, 1.18); headG.add(head);
  headG.add(part(headDarkGeo, mats.dark));
  headG.add(part(inkGeo, inkMat));
  const mouth = part(S(0.08, 16, 10), inkMat); mouth.position.set(0, -0.28, 1.07); mouth.scale.set(1, 0.02, 0.45); mouth.visible = false; headG.add(mouth);
  const itemAnchor = new THREE.Group(); itemAnchor.position.set(0, 0.55, 0.05); itemAnchor.scale.setScalar(1.35); headG.add(itemAnchor);
  return { root, bodyG, body, feet, arms, paws, headPivot, headG, mouth, itemAnchor };
}
